import React from 'react';
import { formatDubai } from '../../../../utils/dubaiTime';
import './pendingReviewBanner.css';

const REQUEST_LABELS = {
  create_booking: 'New booking request',
  edit_booking: 'Booking edit request',
  cancel_booking: 'Cancellation request',
  create_property: 'New property request',
  edit_property: 'Property edit request',
  edit_client_profile: 'Profile edit request',
  edit_client_tasks: 'Tasks edit request',
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const d = Math.floor(diff / 86400000);
  if (d >= 1) return `${d}d ago`;
  const h = Math.floor(diff / 3600000);
  if (h >= 1) return `${h}h ago`;
  const m = Math.floor(diff / 60000);
  return m >= 1 ? `${m}m ago` : 'just now';
};

// Asia/Dubai render (item 8) — same "19 Jul 26" visual as before.
const fmtDate = (iso) => (iso ? formatDubai(iso, 'dd MMM yy') : '');

/**
 * Firm-warn gate (payments Phase 3): when the selected client already has
 * pending requests and/or Pending bookings, steer the admin to review those
 * first. Non-blocking — the admin can acknowledge and still create the booking.
 * `summary` = { requests, pending_bookings, counts:{requests,bookings} }.
 */
export default function PendingReviewBanner({ summary, acknowledged, onToggleAck }) {
  if (!summary) return null;
  const reqCount = summary.counts?.requests || 0;
  const bkCount = summary.counts?.bookings || 0;
  if (reqCount + bkCount === 0) return null;

  return (
    <div className="prb">
      <div className="prb__head">
        <span className="prb__icon" aria-hidden="true">⚠️</span>
        <div className="prb__headText">
          This client has {reqCount > 0 && <strong>{reqCount} pending request{reqCount > 1 ? 's' : ''}</strong>}
          {reqCount > 0 && bkCount > 0 && ' and '}
          {bkCount > 0 && <strong>{bkCount} pending booking{bkCount > 1 ? 's' : ''}</strong>}.
          Review these first — it keeps the wallet balance right and avoids duplicate or stale bookings.
        </div>
      </div>

      {reqCount > 0 && (
        <div className="prb__group">
          <div className="prb__groupTitle">Pending requests</div>
          <ul className="prb__list">
            {summary.requests.map((r) => (
              <li key={`r-${r.id}`} className="prb__item">
                <span className="prb__itemName">{REQUEST_LABELS[r.request_type] || r.request_type}</span>
                <span className="prb__itemMeta">{timeAgo(r.created_at)}</span>
              </li>
            ))}
          </ul>
          <a className="prb__link" href="/" target="_blank" rel="noreferrer">
            Open the Pending Requests panel →
          </a>
        </div>
      )}

      {bkCount > 0 && (
        <div className="prb__group">
          <div className="prb__groupTitle">Pending bookings</div>
          <ul className="prb__list">
            {summary.pending_bookings.map((b) => (
              <li key={`b-${b.id}`} className="prb__item">
                <a className="prb__itemLink" href={`/bookings/${b.id}`} target="_blank" rel="noreferrer">
                  Booking #{b.id}
                </a>
                <span className="prb__itemMeta">
                  {fmtDate(b.booking_start)}{b.pending_reason ? ` · ${b.pending_reason}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <label className="prb__ack">
        <input type="checkbox" checked={!!acknowledged} onChange={(e) => onToggleAck(e.target.checked)} />
        <span>I’ve reviewed these — create this booking anyway.</span>
      </label>
    </div>
  );
}
